import { Icon } from '@main/core-ui';
import { AccessiblePopover, usePopover } from '@main/pc-core-components';
import type { LanguageKey } from '@transcend-io/internationalization';
import React from 'react';

import { OptionsWrapper, StyledButton, StyledOption } from './wrappers';

export interface IToggleOption {
  /** The option's value */
  value: LanguageKey;
  /** The option's label */
  label: string;
}

export interface LocalePopoverProps {
  /** HTML id */
  id?: string;
  /** Callback function on toggle click */
  onToggle: (language: LanguageKey) => void;
  /** The current value */
  currentValue?: LanguageKey;
  /** The other locale options */
  options?: IToggleOption[];
  /** ARIA label */
  label?: string;
}

/**
 * The Privacy Center language picker
 */
export const LocalePopover: React.FC<
  React.PropsWithChildren<LocalePopoverProps>
> = ({ onToggle, currentValue, options, id, label }) => {
  const popover = usePopover();
  const { openPopover } = popover;

  // Find corresponding label for button text of currently selected LanguageKey value
  const getLabel = (currentValue: LanguageKey): string | undefined => {
    const option = options?.find(({ value }) => value === currentValue);
    return option?.label;
  };

  return (
    <>
      <AccessiblePopover popover={popover} yPosition="top" xPosition="right">
        <div style={{ padding: 4, maxHeight: 240, overflow: 'auto' }}>
          <OptionsWrapper id="select-language" role="menu">
            {options?.map(({ value, label }) => (
              <li role="menuitem" key={value}>
                <StyledOption
                  onClick={() => onToggle(value)}
                  $current={value === currentValue}
                  id={value}
                >
                  {label}
                </StyledOption>
              </li>
            ))}
          </OptionsWrapper>
        </div>
      </AccessiblePopover>
      <StyledButton
        aria-haspopup="true"
        aria-controls="select-language"
        aria-label={label}
        onClick={openPopover}
        id={id}
      >
        <div style={{ marginRight: 4, display: 'flex' }}>
          <Icon type="language-translate" size={20} />
        </div>
        {currentValue && getLabel(currentValue)}
      </StyledButton>
    </>
  );
};
